import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSucursal } from '../context/SucursalContext';
import { cajaApi } from '../services/api';
import { imprimirTicketCierre } from '../utils/printHelpers';
import { formatDate, formatDateTime } from '../utils/dateHelpers';

const CortesCaja = () => {
  const { user, isAdmin } = useAuth();
  const { sucursales, sucursalActual, cambiarSucursal } = useSucursal();
  const tiendaIdParaCaja = isAdmin() ? (sucursalActual?.id || 'todas') : user?.tienda_id;
  const [tab, setTab] = useState('verificar'); // verificar, cortes, historial
  const [loading, setLoading] = useState(false);

  // Verificar Metodos
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0]);
  const [totalesMetodo, setTotalesMetodo] = useState([]);
  const [movimientosVerificar, setMovimientosVerificar] = useState([]);

  // Cortes (rango de fechas: suma todas las cajas cerradas entre fecha_desde y fecha_hasta)
  const primerDiaSemana = new Date();
  const diaSemana = primerDiaSemana.getDay(); // 0=domingo
  primerDiaSemana.setDate(primerDiaSemana.getDate() - (diaSemana === 0 ? 6 : diaSemana - 1)); // lunes de esta semana
  const [fechaDesdeCorte, setFechaDesdeCorte] = useState(primerDiaSemana.toISOString().split('T')[0]);
  const [fechaHastaCorte, setFechaHastaCorte] = useState(new Date().toISOString().split('T')[0]);
  const [cortes, setCortes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Historial de Cierres
  const primerDiaMes = new Date();
  primerDiaMes.setDate(1);
  const [fechaDesdeHist, setFechaDesdeHist] = useState(primerDiaMes.toISOString().split('T')[0]);
  const [fechaHastaHist, setFechaHastaHist] = useState(new Date().toISOString().split('T')[0]);
  const [cierres, setCierres] = useState([]);
  const [showMovModal, setShowMovModal] = useState(false);
  const [movimientosCierre, setMovimientosCierre] = useState([]);
  const [cierreSeleccionado, setCierreSeleccionado] = useState(null);
  const [loadingMov, setLoadingMov] = useState(false);

  useEffect(() => {
    if (tab === 'verificar') {
      cargarMetodos();
    } else if (tab === 'cortes') {
      cargarCortes();
    } else if (tab === 'historial') {
      cargarCierres();
    }
  }, [tab, tiendaIdParaCaja]);

  const cargarMetodos = async () => {
    try {
      setLoading(true);
      const data = await cajaApi.verificarMetodos({ tienda_id: tiendaIdParaCaja, fecha_inicio: fechaInicio, fecha_fin: fechaFin });
      setTotalesMetodo(data.totales || []);
      setMovimientosVerificar(data.movimientos || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVerificado = async (mov) => {
    const nuevoValor = !mov.verificado;
    setMovimientosVerificar(prev => prev.map(m => m.id === mov.id ? { ...m, verificado: nuevoValor } : m));
    try {
      await cajaApi.marcarVerificado(mov.id, nuevoValor);
    } catch (error) {
      console.error('Error:', error);
      setMovimientosVerificar(prev => prev.map(m => m.id === mov.id ? { ...m, verificado: !nuevoValor } : m));
      alert('No se pudo actualizar la verificación. Intenta de nuevo.');
    }
  };

  const cargarCortes = async () => {
    try {
      setLoading(true);
      const data = await cajaApi.reporteCortes({ tienda_id: tiendaIdParaCaja, tipo: 'rango', fecha_desde: fechaDesdeCorte, fecha_hasta: fechaHastaCorte });
      setCortes(data || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCortesSubmit = (e) => {
    e.preventDefault();
    cargarCortes();
  };

  const cargarCierres = async () => {
    try {
      setLoading(true);
      const data = await cajaApi.listarCortes({ tienda_id: tiendaIdParaCaja, fecha_desde: fechaDesdeHist, fecha_hasta: fechaHastaHist });
      setCierres(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHistorialSubmit = (e) => {
    e.preventDefault();
    cargarCierres();
  };

  const handleVerMovimientosCierre = async (cierre) => {
    setCierreSeleccionado(cierre);
    setShowMovModal(true);
    setLoadingMov(true);
    try {
      const data = await cajaApi.getMovimientos({ caja_id: cierre.id });
      setMovimientosCierre(data || []);
    } catch (error) {
      console.error('Error:', error);
      setMovimientosCierre([]);
    } finally {
      setLoadingMov(false);
    }
  };

  const handleImprimirCierre = (c) => {
    imprimirTicketCierre(
      { ...c, estado: 'cerrada', totales: {
        ingresos_efectivo: c.monto_efectivo,
        ingresos_transferencia: c.monto_transferencias,
        ingresos_pos: c.monto_pos,
        ingresos_cheque: c.monto_cheques,
        total_egresos: c.monto_egresos
      } },
      c.nombre_tienda || sucursales.find(s => s.id === tiendaIdParaCaja)?.nombre
    );
  };

  const formatMoney = (amount) => {
    return `Q${parseFloat(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;
  };

  const formatPeriodo = (periodo) => {
    if (!periodo) return '-';
    const [desde, hasta] = String(periodo).split(' al ');
    if (!desde && !hasta) return '-';
    return `${formatDate(desde)} al ${formatDate(hasta)}`;
  };

  const handleImprimir = (c) => {
    const cajaFake = {
      id: c.periodo,
      fecha_apertura: c.inicio_periodo,
      fecha_cierre: c.fin_periodo,
      estado: 'CERRADA',
      monto_apertura: c.total_apertura,
      monto_cierre: c.total_cierre,
      monto_efectivo: c.total_efectivo,
      monto_transferencias: c.total_transferencias,
      monto_pos: c.total_pos,
      monto_cheques: c.total_cheques,
      monto_egresos: c.total_egresos,
      totales: {
        ingresos_efectivo: c.total_efectivo,
        ingresos_transferencia: c.total_transferencias,
        ingresos_pos: c.total_pos,
        ingresos_cheque: c.total_cheques,
        total_egresos: c.total_egresos
      }
    };
    imprimirTicketCierre(cajaFake, `Corte Temporal (${formatPeriodo(c.periodo)}) - ${sucursales.find(s => s.id === (tiendaIdParaCaja || user?.tienda_id))?.nombre || 'General'}`);
  };

  const paginatedCortes = cortes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(cortes.length / itemsPerPage);

  if (!isAdmin() && !tiendaIdParaCaja) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Cortes y Verificación de Caja</h1>
        </div>
        <div className="card" style={{ textAlign: 'center', color: 'var(--danger-color)' }}>
          <h3>Sucursal no asignada</h3>
          <p>Tu usuario no tiene una sucursal asignada en el sistema. Debes tener una sucursal para poder generar cortes de caja.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Cortes y Verificación de Caja</h1>
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button 
              className={`btn ${tab === 'verificar' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTab('verificar')}
            >
              Verificar Transferencias / Tarjetas
            </button>
            <button
              className={`btn ${tab === 'cortes' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTab('cortes')}
            >
              Corte Temporal
            </button>
            <button
              className={`btn ${tab === 'historial' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTab('historial')}
            >
              Historial de Cierres
            </button>
          </div>
        </div>
        {isAdmin() && (
          <div style={{ minWidth: '200px' }}>
            <select
              className="form-control"
              value={sucursalActual?.id || 'todas'}
              onChange={(e) => cambiarSucursal(e.target.value)}
            >
              <option value="todas">Todas las Sucursales</option>
              {sucursales.map(s => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {tab === 'verificar' && (
        <div className="card">
          <form style={{ display: 'flex', gap: '20px', marginBottom: '20px', alignItems: 'flex-end' }} onSubmit={(e) => { e.preventDefault(); cargarMetodos(); }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Fecha Inicio</label>
              <input type="date" className="form-control" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Fecha Fin</label>
              <input type="date" className="form-control" value={fechaFin} onChange={e => setFechaFin(e.target.value)} />
            </div>
            <button type="submit" className="btn btn-primary">Buscar</button>
          </form>

          {loading ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : (
            <>
              <h3 className="card-title" style={{ marginBottom: '12px' }}>Totales por Método de Pago</h3>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Método de Pago</th>
                      <th style={{ textAlign: 'right' }}>Total Ingresos</th>
                      <th style={{ textAlign: 'right' }}>Total Egresos</th>
                      <th style={{ textAlign: 'right' }}>Neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {totalesMetodo.length === 0 ? (
                      <tr><td colSpan="4" style={{ textAlign: 'center', color: 'var(--text-light)' }}>No hay datos para este rango de fechas</td></tr>
                    ) : (
                      totalesMetodo.map((t, idx) => (
                        <tr key={idx}>
                          <td>{t.metodo_pago}</td>
                          <td style={{ textAlign: 'right', color: 'var(--success-color)' }}>
                            {formatMoney(t.total_ingresos)} {t.cantidad_ingresos > 0 && <small style={{ color: 'var(--text-light)' }}>({t.cantidad_ingresos})</small>}
                          </td>
                          <td style={{ textAlign: 'right', color: 'var(--danger-color)' }}>
                            {formatMoney(t.total_egresos)} {t.cantidad_egresos > 0 && <small style={{ color: 'var(--text-light)' }}>({t.cantidad_egresos})</small>}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>
                            {formatMoney(parseFloat(t.total_ingresos) - parseFloat(t.total_egresos))}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <h3 className="card-title" style={{ marginTop: '24px', marginBottom: '4px' }}>Movimientos a Verificar</h3>
              <p style={{ color: 'var(--text-light)', fontSize: '13px', marginBottom: '12px' }}>
                Marca cada transferencia, pago POS o cheque (ingreso o egreso) al confirmarlo contra el estado de cuenta del banco o la terminal.
              </p>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Concepto</th>
                      <th>Método</th>
                      <th>Cuenta / Terminal</th>
                      <th style={{ textAlign: 'right' }}>Monto</th>
                      <th style={{ textAlign: 'center' }}>Verificado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimientosVerificar.length === 0 ? (
                      <tr><td colSpan="7" style={{ textAlign: 'center', color: 'var(--text-light)' }}>No hay movimientos en este rango de fechas</td></tr>
                    ) : (
                      movimientosVerificar.map((m) => (
                        <tr key={m.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(m.fecha)}</td>
                          <td>
                            <span className={`status-badge ${m.tipo === 'ingreso' ? 'status-ingreso' : 'status-egreso'}`}>
                              {m.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                            </span>
                          </td>
                          <td>{m.concepto}</td>
                          <td>{m.metodo_pago}</td>
                          <td>
                            {m.metodo_pago === 'Transferencia' ? (m.cuenta_nombre ? `${m.cuenta_nombre} (${m.cuenta_banco})` : '-') :
                             m.metodo_pago === 'POS' ? (m.pos_nombre || '-') : (m.comprobante || '-')}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: m.tipo === 'ingreso' ? 'var(--success-color)' : 'var(--danger-color)' }}>
                            {m.tipo === 'ingreso' ? '+' : '-'} {formatMoney(m.monto)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={!!m.verificado}
                              onChange={() => handleToggleVerificado(m)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'cortes' && (
        <div className="card">
          <p style={{ color: 'var(--text-light)', fontSize: '13px', marginBottom: '16px' }}>
            Suma todas las cajas ya cerradas entre la fecha desde y la fecha hasta en un solo total consolidado.
          </p>
          <form style={{ display: 'flex', gap: '20px', marginBottom: '20px', alignItems: 'flex-end' }} onSubmit={handleCortesSubmit}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Fecha Desde</label>
              <input type="date" className="form-control" value={fechaDesdeCorte} onChange={e => setFechaDesdeCorte(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Fecha Hasta</label>
              <input type="date" className="form-control" value={fechaHastaCorte} onChange={e => setFechaHastaCorte(e.target.value)} />
            </div>
            <button type="submit" className="btn btn-primary">Generar</button>
          </form>

          {loading ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Periodo</th>
                    <th>Cajas</th>
                    <th style={{ textAlign: 'right' }}>Apertura</th>
                    <th style={{ textAlign: 'right' }}>Ingresos</th>
                    <th style={{ textAlign: 'right' }}>Egresos Totales</th>
                    <th style={{ textAlign: 'left' }}>Detalle de Egresos</th>
                    <th style={{ textAlign: 'right' }}>Cierre Total</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCortes.length === 0 ? (
                    <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text-light)' }}>No hay cortes en este periodo</td></tr>
                  ) : (
                    paginatedCortes.map((c, idx) => (
                      <tr key={idx}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatPeriodo(c.periodo)}</td>
                        <td>{c.total_cajas}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoney(c.total_apertura)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <small style={{ display: 'block' }}>Efec: {formatMoney(c.total_efectivo)}</small>
                          <small style={{ display: 'block' }}>Transf: {formatMoney(c.total_transferencias)}</small>
                          <small style={{ display: 'block' }}>POS: {formatMoney(c.total_pos)}</small>
                          <small style={{ display: 'block' }}>Cheques: {formatMoney(c.total_cheques)}</small>
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--danger-color)' }}>-{formatMoney(c.total_egresos)}</td>
                        <td style={{ textAlign: 'left' }}>
                          {c.egresos_detalle && c.egresos_detalle.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.85em' }}>
                              {c.egresos_detalle.map((ed, i) => (
                                <li key={i}>{ed.categoria}: {formatMoney(ed.total)}</li>
                              ))}
                            </ul>
                          ) : (
                            <span style={{ fontSize: '0.85em', color: 'var(--text-light)' }}>Sin detalle</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(c.total_cierre)}</td>
                        <td>
                          <button className="btn btn-secondary btn-sm" onClick={() => handleImprimir(c)}>Imprimir</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                  <button 
                    className="btn btn-secondary" 
                    disabled={currentPage === 1} 
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    Anterior
                  </button>
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    Página {currentPage} de {totalPages}
                  </span>
                  <button 
                    className="btn btn-secondary" 
                    disabled={currentPage === totalPages} 
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'historial' && (
        <div className="card">
          <form style={{ display: 'flex', gap: '20px', marginBottom: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }} onSubmit={handleHistorialSubmit}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Fecha Desde</label>
              <input type="date" className="form-control" value={fechaDesdeHist} onChange={e => setFechaDesdeHist(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Fecha Hasta</label>
              <input type="date" className="form-control" value={fechaHastaHist} onChange={e => setFechaHastaHist(e.target.value)} />
            </div>
            <button type="submit" className="btn btn-primary">Buscar</button>
          </form>

          {loading ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Fecha Apertura</th>
                    <th>Fecha Cierre</th>
                    <th>Usuario</th>
                    <th style={{ textAlign: 'right' }}>Apertura</th>
                    <th style={{ textAlign: 'right' }}>Efectivo</th>
                    <th style={{ textAlign: 'right' }}>Transf.</th>
                    <th style={{ textAlign: 'right' }}>POS</th>
                    <th style={{ textAlign: 'right' }}>Cheques</th>
                    <th style={{ textAlign: 'right' }}>Egresos</th>
                    <th style={{ textAlign: 'right' }}>Cierre Declarado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cierres.length === 0 ? (
                    <tr><td colSpan="11" style={{ textAlign: 'center', color: 'var(--text-light)' }}>No hay cierres en este rango de fechas</td></tr>
                  ) : (
                    cierres.map((c) => (
                      <tr key={c.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(c.fecha_apertura)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(c.fecha_cierre)}</td>
                        <td style={{ fontSize: '13px', color: 'var(--text-light)' }}>{c.usuario_nombre || '-'}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoney(c.monto_apertura)}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoney(c.monto_efectivo)}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoney(c.monto_transferencias)}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoney(c.monto_pos)}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoney(c.monto_cheques)}</td>
                        <td style={{ textAlign: 'right', color: 'var(--danger-color)' }}>{formatMoney(c.monto_egresos)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(c.monto_cierre)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => handleVerMovimientosCierre(c)}>Ver Movimientos</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => handleImprimirCierre(c)}>Imprimir</button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal Movimientos de un Cierre */}
      {showMovModal && (
        <div className="modal-overlay" onClick={() => setShowMovModal(false)}>
          <div className="modal" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                Movimientos del Cierre {cierreSeleccionado ? `#${cierreSeleccionado.id}` : ''}
              </h2>
              <button className="modal-close" onClick={() => setShowMovModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {loadingMov ? (
                <div className="loading"><div className="spinner"></div></div>
              ) : movimientosCierre.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay movimientos registrados en este cierre</p>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Tipo</th>
                        <th>Concepto</th>
                        <th>Método</th>
                        <th style={{ textAlign: 'right' }}>Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movimientosCierre.map(m => (
                        <tr key={m.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(m.fecha)}</td>
                          <td>
                            <span className={`status-badge ${m.tipo === 'ingreso' ? 'status-ingreso' : 'status-egreso'}`}>
                              {m.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                            </span>
                          </td>
                          <td>
                            {m.concepto}
                            {m.categoria_nombre && <small style={{ color: 'var(--text-light)' }}> [{m.categoria_nombre}]</small>}
                          </td>
                          <td>
                            {m.metodo_pago}
                            {m.cuenta_nombre && <small style={{ color: 'var(--text-light)' }}> ({m.cuenta_nombre})</small>}
                            {m.pos_nombre && <small style={{ color: 'var(--text-light)' }}> ({m.pos_nombre})</small>}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: m.tipo === 'ingreso' ? 'var(--success-color)' : 'var(--danger-color)' }}>
                            {m.tipo === 'ingreso' ? '+' : '-'} {formatMoney(m.monto)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CortesCaja;
